import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, FileText, Users, Target, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const Home = () => {
  const services = [
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "CFO Externo",
      description: "Dirección financiera estratégica para empresas que buscan crecer con control y rentabilidad sostenible.",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: "Consultoría Financiera",
      description: "Diagnóstico financiero integral, análisis de rentabilidad y estrategias de aceleración del valor.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Centro de Servicios Contables",
      description: "Contabilidad, nómina, facturación electrónica y gestión administrativa completa para PYMEs.",
      image: "https://images.unsplash.com/photo-1707157284454-553ef0a4ed0d?crop=entropy&cs=srgb&fm=jpg&q=85"
    }
  ];

  const benefits = [
    "Control financiero estratégico sin costos de nómina fija",
    "Decisiones informadas basadas en datos reales",
    "Cumplimiento contable y fiscal profesional",
    "Flujo de caja optimizado y rentabilidad medible",
    "Acompañamiento personalizado para tu crecimiento"
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        {/* Meta Tags Básicos */}
        <title>Sinergia Empresarial CSCE | Consultoría Financiera y Servicios Contables para PYMEs</title>
        <meta name="description" content="CFO externo, consultoría financiera y servicios contables especializados para PYMEs en Colombia. Estrategia financiera profesional para empresas en crecimiento." />
        <meta name="keywords" content="CFO externo, consultoría financiera, servicios contables, PYMEs Colombia, contador público, asesoría financiera, flujo de caja, diagnóstico financiero" />
        <link rel="canonical" href="https://www.sinergiaempresarialcsce.com/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sinergiaempresarialcsce.com/" />
        <meta property="og:title" content="Sinergia Empresarial CSCE | Consultoría Financiera para PYMEs" />
        <meta property="og:description" content="CFO externo, consultoría financiera y servicios contables especializados para empresas en crecimiento en Colombia." />
        <meta property="og:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        <meta property="og:locale" content="es_CO" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.sinergiaempresarialcsce.com/" />
        <meta name="twitter:title" content="Sinergia Empresarial CSCE | Consultoría Financiera para PYMEs" />
        <meta name="twitter:description" content="CFO externo, consultoría financiera y servicios contables especializados para empresas en crecimiento." />
        <meta name="twitter:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Sinergia Empresarial CSCE",
            "description": "Consultoría financiera y servicios contables especializados para PYMEs",
            "url": "https://www.sinergiaempresarialcsce.com",
            "logo": "https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png",
            "image": "https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "CO",
              "addressLocality": "Colombia"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Colombia"
            },
            "serviceType": [
              "CFO Externo",
              "Consultoría Financiera",
              "Servicios Contables",
              "Diagnóstico Financiero",
              "Flujo de Caja"
            ],
            "knowsAbout": [
              "Finanzas Corporativas",
              "Contabilidad",
              "Estrategia Financiera",
              "Gestión de PYMEs"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section relative">
        <div className="hero-background">
          <img 
            src="/hero.jpg"
            alt="Consultoría Financiera Profesional"
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 className="hero-title text-white mb-6">
              Estrategia financiera y operación contable para que tu empresa crezca con control
            </h1>
            <p className="body-large text-white/90 mb-8 max-w-2xl">
              Dirección financiera profesional y servicios contables especializados para PYMEs que buscan escalar de forma sostenible.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contacto">
                <Button className="btn-primary px-8 py-6 text-lg">
                  Agenda tu diagnóstico
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/servicios">
                <Button className="btn-primary px-8 py-6 text-lg">
                  Conoce nuestros servicios
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="heading-2 text-4xl font-bold mb-4 text-sinergia-gris-oscuro">
              Servicios diseñados para tu crecimiento
            </h2>
            <p className="body-medium text-gray-600 max-w-2xl mx-auto">
              Soluciones integrales que combinan visión estratégica y ejecución operativa para empresas en crecimiento.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="text-sinergia-azul mb-4">
                    {service.icon}
                  </div>
                  <h3 className="heading-4 text-xl font-semibold mb-3 text-sinergia-gris-oscuro">
                    {service.title}
                  </h3>
                  <p className="body-small text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <Link to="/servicios" className="link-text inline-flex items-center font-semibold text-sinergia-azul hover:text-sinergia-verde">
                    Ver detalles
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-2 text-4xl font-bold mb-6 text-sinergia-gris-oscuro">
                ¿Por qué elegir Sinergia Empresarial?
              </h2>
              <p className="body-medium text-gray-600 mb-8">
                Más que un proveedor de servicios, somos tu aliado estratégico en el camino hacia la rentabilidad y el crecimiento sostenible.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-sinergia-verde-claro mr-3 flex-shrink-0 mt-1" />
                    <span className="body-small text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/quienes-somos">
                  <Button variant="outline" className="btn-secondary">
                    Conoce más sobre nosotros
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Equipo profesional de consultoría"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-sinergia-azul text-white">
        <div className="container mx-auto px-6 text-center">
          <Target className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="heading-2 text-4xl font-bold mb-4">
            ¿Listo para transformar tus finanzas?
          </h2>
          <p className="body-large mb-8 max-w-2xl mx-auto">
            Agenda una consulta sin compromiso y descubre cómo podemos ayudarte a alcanzar tus objetivos de negocio.
          </p>
          <Link to="/contacto">
            <Button className="bg-white text-sinergia-azul hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-full">
              Solicita tu diagnóstico gratuito
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
